import Controller from "sap/ui/core/mvc/Controller";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import DateFormat from "sap/ui/core/format/DateFormat";
import Logger from "sap/base/Log";
import type ShellController from "niteroi/controllers/Shell.controller";

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

enum Themes {
    "sap_horizon" = "sap_horizon",
    "sap_horizon_dark" = "sap_horizon_dark",
    "sap_horizon_hcb" = "sap_horizon_hcb",
    "sap_horizon_hcw" = "sap_horizon_hcw",
}

const formatter = {
    /**
     * Generic client-side translator for text that lives inside JSON models.
     * The model stores an i18n key, this resolves that key against the active
     * ResourceBundle.
     */
    translate: async function (this: Controller, text: string, key: string): Promise<string> {
        if (!text) {
            return "";
        }
        if (key) {
            const component = this.getOwnerComponent();
            const localizationModel = component?.getModel("i18n") as ResourceModel | undefined;
            const resourceBundle = await localizationModel?.getResourceBundle();
            return resourceBundle?.getText(key) ?? text ?? "";
        }
        return text ?? "";
    },

    showInstallUninstallText(installed: boolean, key: string, system: string, uninstall: string, install: string): string {
        if (key === "system") {
            return system
        }
        return installed ? uninstall : install;
    },

    showPackageStatusText(installed: boolean, key: string, statusDefault: string, statusInstalled: string, statusNotInstalled: string): string {
        if (key === "system") {
            return statusDefault;
        }
        return installed ? statusInstalled : statusNotInstalled;
    },

    typeInstallUninstallButton: (installed: boolean, key: string): string => {
        if (key === "system") {
            return "Transparent";
        }
        return installed ? "Negative" : "Success";
    },

    getInitials: (firstName: string, lastName: string): string => {
        const first = firstName ? firstName.charAt(0) : "";
        const last = lastName ? lastName.charAt(0) : "";
        return (first + last).toUpperCase();
    },

    setAvatarSrc: (profilePhoto: string, sessionId: string): string | null => {
        if (profilePhoto) {
            return profilePhoto;
        }
        if (sessionId) {
            return null;
        }
        if (!profilePhoto && !sessionId) {
            return "sap-icon://visits";
        }
    },

    formatThemeName: async function (this: Controller, themeName: string | undefined, appTitle: string): Promise<string> {
        if (!themeName) {
            return "";
        }

        const component = this.getOwnerComponent();
        Logger.info("### Works: using i18n refresh from app's title", appTitle, component.getMetadata().getComponentName());

        const localizationModel = component?.getModel("i18n") as ResourceModel | undefined;
        const resourceBundle = await localizationModel?.getResourceBundle();

        if (!resourceBundle) {
            return themeName;
        }
        let text: string;
        switch (themeName) {
            case "sap_horizon_dark":
                text = "themeDark";
                break;
            case "sap_horizon_hcw":
                text = "themeHighContrastLight";
                break;
            case "sap_horizon_hcb":
                text = "themeHighContrastDark";
                break;
            case "sap_horizon":
            default:
                text = "themeLight";
        }
        return resourceBundle.getText(text ?? "");
    },

    formatProductSwitchTarget: (key: string, delimiter: string = '-', href: string = 'launchpad'): string => {
        return key + delimiter + href;
    },

    formatUnit(count: number, singular: string, plural: string): string {
        return count === 1 ? singular : plural;
    },

    formatCountAndUnit(count: number, singular: string, plural: string): string {
        const text = count === 1 ? singular : plural;
        return `${count} ${text}`;
    },

    /**
     * Number of whole days remaining until the given target date.
     * Never returns a negative value once the date has passed.
     */
    daysUntil(targetDate: string | number | Date): number {
        if (!targetDate) {
            return 0;
        }
        const target = new Date(targetDate).getTime();
        const remaining = Math.ceil((target - Date.now()) / MILLISECONDS_PER_DAY);
        return Math.max(0, remaining);
    },

    /**
     * Countdown label for the promotional edition, e.g. "Only 40 days left!".
     * Bound with `.bind($controller)` so `this` provides the i18n resource bundle.
     */
    formatDaysRemaining: async function (this: Controller, targetDate: string, appTitle: string): Promise<string> {
        if (!targetDate) {
            return "";
        }

        const component = this.getOwnerComponent();
        Logger.info("### Works: using i18n refresh from app's title", appTitle, component.getMetadata().getComponentName());

        const localizationModel = component?.getModel("i18n") as ResourceModel | undefined;
        const resourceBundle = await localizationModel?.getResourceBundle();

        if (!resourceBundle) {
            return targetDate;
        }

        const days = formatter.daysUntil(targetDate);
        return resourceBundle.getText("pricingEarlyStarterCountdown", [days]);
    },

    /**
     * Availability label for the standard edition, e.g. "Starting from 1 September 2026!".
     * The date is rendered in the active locale via DateFormat.
     * Bound with `.bind($controller)` so `this` provides the i18n resource bundle.
     */
    formatLaunchDate: async function (this: Controller, launchDate: string, appTitle: string): Promise<string> {
        if (!launchDate) {
            return "";
        }

        const component = this.getOwnerComponent();
        Logger.info("### Works: using i18n refresh from app's title", appTitle, component.getMetadata().getComponentName());

        const localizationModel = component?.getModel("i18n") as ResourceModel | undefined;
        const resourceBundle = await localizationModel?.getResourceBundle();

        if (!resourceBundle) {
            return launchDate;
        }

        const dateFormat = DateFormat.getDateInstance({style: "long"});
        const formattedDate = dateFormat.format(new Date(launchDate));
        return resourceBundle.getText("pricingStandardAvailability", [formattedDate]);
    },

    setShellBarLogo(this: ShellController, companyLogo: string, themeName: Themes): string {
        if (companyLogo) {
            return companyLogo;
        }

        const themeLogos: Record<Themes, string> = {
            "sap_horizon": "/images/symbol-rounded-blue.svg",
            "sap_horizon_dark": "/images/symbol-rounded-blue.svg",
            "sap_horizon_hcb": "/images/symbol-white-black.svg",
            "sap_horizon_hcw": "/images/symbol-black-white.svg"
        };

        return themeLogos[themeName] || themeLogos["sap_horizon"];
    }
};

export default formatter;
